import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Phone, Wrench, IndianRupee,
  Receipt, Calendar, Target, Award, Clock, RefreshCw, Filter,
  ArrowUpRight, ArrowDownRight, Activity, PieChartIcon, BarChart3, MessageSquareWarning
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const OwnerDashboard = () => {
  const [period, setPeriod] = useState('30days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Data states
  const [overview, setOverview] = useState(null);
  const [leadsStats, setLeadsStats] = useState(null);
  const [servicesStats, setServicesStats] = useState(null);
  const [paymentsStats, setPaymentsStats] = useState(null);
  const [expensesStats, setExpensesStats] = useState(null);
  const [topSales, setTopSales] = useState(null);
  const [topEngineers, setTopEngineers] = useState(null);
  const [topAttendance, setTopAttendance] = useState(null);
  const [complaintsStats, setComplaintsStats] = useState(null);

  const periodOptions = [
    { value: 'today', label: 'Today' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '15days', label: 'Last 15 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '60days', label: 'Last 60 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom' && fromDate && toDate) {
        params.append('from_date', fromDate);
        params.append('to_date', toDate);
      }

      const [overviewRes, leadsRes, servicesRes, paymentsRes, expensesRes, salesRes, engineersRes, attendanceRes, complaintsRes] = await Promise.all([
        api.get(`/owner-dashboard/overview?${params}`),
        api.get(`/owner-dashboard/leads-stats?${params}`),
        api.get(`/owner-dashboard/services-stats?${params}`),
        api.get(`/owner-dashboard/payments-stats?${params}`),
        api.get(`/owner-dashboard/expenses-stats?${params}`),
        api.get(`/owner-dashboard/top-sales-executives?${params}`),
        api.get(`/owner-dashboard/top-service-engineers?${params}`),
        api.get(`/owner-dashboard/top-attendance?${params}`),
        api.get('/complaints/stats')
      ]);

      setOverview(overviewRes.data);
      setLeadsStats(leadsRes.data);
      setServicesStats(servicesRes.data);
      setPaymentsStats(paymentsRes.data);
      setExpensesStats(expensesRes.data);
      setTopSales(salesRes.data);
      setTopEngineers(engineersRes.data);
      setTopAttendance(attendanceRes.data);
      setComplaintsStats(complaintsRes.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [period, fromDate, toDate]);

  useEffect(() => {
    if (period !== 'custom' || (fromDate && toDate)) {
      fetchDashboardData();
    }
  }, [period, fromDate, toDate, fetchDashboardData]);

  const formatCurrency = (value) => {
    return `₹${value?.toLocaleString('en-IN') || 0}`;
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue' }) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      amber: 'bg-amber-100 text-amber-600',
      red: 'bg-red-100 text-red-600',
      purple: 'bg-purple-100 text-purple-600',
      cyan: 'bg-cyan-100 text-cyan-600'
    };

    return (
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500">{title}</p>
              <p className="text-2xl font-bold mt-1">{value}</p>
              {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
              {trend && (
                <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                  {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  <span>{trendValue}</span>
                </div>
              )}
            </div>
            <div className={`p-3 rounded-lg ${colors[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Owner Dashboard</h1>
          <p className="text-sm md:text-base text-gray-600">Business analytics and performance metrics</p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-3 items-end">
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 md:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {period === 'custom' && (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-32 md:w-36"
                />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-32 md:w-36"
                />
              </div>
            </>
          )}
          <Button onClick={fetchDashboardData} variant="outline" size="sm" className="md:size-default">
            <RefreshCw className={`h-4 w-4 mr-1 md:mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Period Badge */}
      {overview?.period && (
        <Badge variant="outline" className="text-sm">
          <Calendar className="w-3 h-3 mr-1" />
          {overview.period.start} to {overview.period.end}
        </Badge>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="performers">Top Performers</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Overview Stats */}
          <div className="grid grid-cols-4 lg:grid-cols-8 gap-4">
            <StatCard
              title="New Leads"
              value={overview?.overview?.new_leads || 0}
              subtitle={`${overview?.overview?.converted_leads || 0} converted`}
              icon={Phone}
              color="blue"
            />
            <StatCard
              title="Lead Conversion"
              value={`${leadsStats?.summary?.conversion_rate || 0}%`}
              subtitle={`${leadsStats?.summary?.success || 0} of ${leadsStats?.summary?.total || 0}`}
              icon={Target}
              color="green"
            />
            <StatCard
              title="New Customers"
              value={overview?.overview?.new_customers || 0}
              icon={Users}
              color="purple"
            />
            <StatCard
              title="Services"
              value={overview?.overview?.total_services || 0}
              subtitle={`${overview?.overview?.completed_services || 0} completed`}
              icon={Wrench}
              color="cyan"
            />
            <StatCard
              title="Total Revenue"
              value={formatCurrency(overview?.overview?.total_revenue)}
              icon={IndianRupee}
              color="green"
            />
            <StatCard
              title="Collected"
              value={formatCurrency(overview?.overview?.total_collected)}
              subtitle={`₹${(overview?.overview?.total_pending || 0).toLocaleString('en-IN')} pending`}
              icon={Target}
              color="amber"
            />
            <StatCard
              title="Net Profit"
              value={formatCurrency(overview?.overview?.net_profit)}
              subtitle={`Expenses: ₹${(overview?.overview?.total_expenses || 0).toLocaleString('en-IN')}`}
              icon={TrendingUp}
              color={overview?.overview?.net_profit >= 0 ? 'green' : 'red'}
            />
            <StatCard
              title="New Complaints"
              value={complaintsStats?.new || 0}
              subtitle={`${complaintsStats?.total || 0} Total`}
              icon={MessageSquareWarning}
              color={complaintsStats?.new > 0 ? 'red' : 'green'}
            />
            <StatCard
              title="Cancelled Sales"
              value={overview?.overview?.cancelled_sales_count || 0}
              subtitle={`₹${(overview?.overview?.cancelled_sales_amount || 0).toLocaleString('en-IN')}`}
              icon={TrendingDown}
              color="red"
            />
          </div>

          {/* Overview Charts Row */}
          <div className="grid grid-cols-2 gap-6">
            {/* Revenue vs Expenses */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Revenue vs Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={[
                    { name: 'Revenue', value: overview?.overview?.total_collected || 0 },
                    { name: 'Expenses', value: overview?.overview?.total_expenses || 0 },
                    { name: 'Pending', value: overview?.overview?.total_pending || 0 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Bar dataKey="value" fill="#3b82f6">
                      {[0,1,2].map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Leads Funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Leads Conversion</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Converted', value: leadsStats?.summary?.success || 0 },
                        { name: 'In Progress', value: leadsStats?.summary?.on_process || 0 },
                        { name: 'Lost', value: leadsStats?.summary?.failures || 0 }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#f59e0b" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leads Tab */}
        <TabsContent value="leads" className="space-y-6">
          <div className="grid grid-cols-5 gap-4">
            <StatCard title="Total Leads" value={leadsStats?.summary?.total || 0} icon={Phone} color="blue" />
            <StatCard title="Converted" value={leadsStats?.summary?.success || 0} icon={Target} color="green" />
            <StatCard title="Lost" value={leadsStats?.summary?.failures || 0} icon={TrendingDown} color="red" />
            <StatCard title="In Progress" value={leadsStats?.summary?.on_process || 0} icon={Activity} color="amber" />
            <StatCard title="Conversion Rate" value={`${leadsStats?.summary?.conversion_rate || 0}%`} icon={TrendingUp} color="purple" />
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Leads Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" /> Leads Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={leadsStats?.daily_data || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5)} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="total" stackId="1" stroke="#3b82f6" fill="#3b82f6" name="Total" />
                    <Area type="monotone" dataKey="success" stackId="2" stroke="#10b981" fill="#10b981" name="Success" />
                    <Area type="monotone" dataKey="failures" stackId="3" stroke="#ef4444" fill="#ef4444" name="Lost" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Lead Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" /> Lead Sources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={leadsStats?.source_breakdown || []}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {(leadsStats?.source_breakdown || []).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-6">
          <div className="grid grid-cols-5 gap-4">
            <StatCard title="Total Services" value={servicesStats?.summary?.total || 0} icon={Wrench} color="blue" />
            <StatCard title="Completed" value={servicesStats?.summary?.completed || 0} icon={Target} color="green" />
            <StatCard title="Pending" value={servicesStats?.summary?.pending || 0} icon={Clock} color="amber" />
            <StatCard title="In Progress" value={servicesStats?.summary?.on_process || 0} icon={Activity} color="cyan" />
            <StatCard title="Completion Rate" value={`${servicesStats?.summary?.completion_rate || 0}%`} icon={TrendingUp} color="purple" />
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Services by Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Services by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={servicesStats?.type_breakdown || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" />
                    <Bar dataKey="on_process" stackId="a" fill="#f59e0b" name="In Progress" />
                    <Bar dataKey="pending" stackId="a" fill="#ef4444" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Services Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Services Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={servicesStats?.daily_data || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5)} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#3b82f6" name="Total" strokeWidth={2} />
                    <Line type="monotone" dataKey="completed" stroke="#10b981" name="Completed" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-6">
          <div className="grid grid-cols-6 gap-4">
            <StatCard title="Total Sales" value={formatCurrency(paymentsStats?.summary?.total_sales_amount)} icon={IndianRupee} color="blue" />
            <StatCard title="Collected" value={formatCurrency(paymentsStats?.summary?.total_paid)} icon={Target} color="green" />
            <StatCard title="Pending" value={formatCurrency(paymentsStats?.summary?.total_pending)} icon={Clock} color="amber" />
            <StatCard title="Long Pending" value={formatCurrency(paymentsStats?.summary?.long_pending_amount)} subtitle=">30 days overdue" icon={TrendingDown} color="red" />
            <StatCard title="Collection Rate" value={`${paymentsStats?.summary?.collection_rate || 0}%`} icon={TrendingUp} color="purple" />
            <StatCard title="Pending Count" value={paymentsStats?.summary?.pending_count || 0} subtitle={`${paymentsStats?.summary?.long_pending_count || 0} overdue`} icon={Receipt} color="cyan" />
          </div>

          <div className="grid grid-cols-3 gap-6">
            {/* Invoice vs Cash Bill */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Invoice vs Cash Bill</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={[
                    { 
                      name: 'GST Invoice', 
                      paid: paymentsStats?.invoice_vs_qcb?.invoices?.paid_amount || 0,
                      pending: paymentsStats?.invoice_vs_qcb?.invoices?.pending_amount || 0
                    },
                    { 
                      name: 'Cash Bill', 
                      paid: paymentsStats?.invoice_vs_qcb?.cash_bills?.paid_amount || 0,
                      pending: paymentsStats?.invoice_vs_qcb?.cash_bills?.pending_amount || 0
                    }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="paid" fill="#10b981" name="Paid" />
                    <Bar dataKey="pending" fill="#ef4444" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Payment Mode Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment Mode Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={paymentsStats?.mode_breakdown || []}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="amount"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(paymentsStats?.mode_breakdown || []).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Collections Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Collections Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={paymentsStats?.daily_data || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5)} />
                    <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Area type="monotone" dataKey="amount" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Amount" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            <StatCard title="Total Expenses" value={formatCurrency(expensesStats?.summary?.total_amount)} icon={Receipt} color="red" />
            <StatCard title="Total Entries" value={expensesStats?.summary?.total_count || 0} icon={Activity} color="blue" />
            <StatCard title="Avg per Expense" value={formatCurrency(expensesStats?.summary?.avg_per_expense)} icon={TrendingUp} color="amber" />
            <StatCard title="Categories" value={expensesStats?.header_breakdown?.length || 0} icon={PieChartIcon} color="purple" />
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Expenses by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Expenses by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={expensesStats?.header_breakdown || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Bar dataKey="amount" fill="#ef4444" name="Amount">
                      {(expensesStats?.header_breakdown || []).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Expenses Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Expenses Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={expensesStats?.daily_data || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5)} />
                    <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Area type="monotone" dataKey="amount" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="Amount" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Expense Payment Mode */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Expenses by Payment Mode</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4">
                {(expensesStats?.mode_breakdown || []).map((mode, idx) => (
                  <div key={mode.name} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">{mode.name}</p>
                    <p className="text-xl font-bold">{formatCurrency(mode.amount)}</p>
                    <p className="text-xs text-gray-400">{mode.count} entries</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Performers Tab */}
        <TabsContent value="performers" className="space-y-6">
          <div className="grid grid-cols-3 gap-6">
            {/* Top Sales Executives */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-500" /> Top Sales Executives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-center">Leads</TableHead>
                      <TableHead className="text-center">Converted</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(topSales?.executives || []).map((exec, idx) => (
                      <TableRow key={exec.id}>
                        <TableCell>
                          {idx < 3 ? (
                            <Badge className={idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'}>
                              {idx + 1}
                            </Badge>
                          ) : idx + 1}
                        </TableCell>
                        <TableCell className="font-medium">{exec.name}</TableCell>
                        <TableCell className="text-center">{exec.total_leads}</TableCell>
                        <TableCell className="text-center text-green-600 font-medium">{exec.converted}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline">{exec.conversion_rate}%</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!topSales?.executives || topSales.executives.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-4">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Top Service Engineers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-blue-500" /> Top Service Engineers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">Done</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(topEngineers?.engineers || []).map((eng, idx) => (
                      <TableRow key={eng.id}>
                        <TableCell>
                          {idx < 3 ? (
                            <Badge className={idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'}>
                              {idx + 1}
                            </Badge>
                          ) : idx + 1}
                        </TableCell>
                        <TableCell className="font-medium">{eng.name}</TableCell>
                        <TableCell className="text-center">{eng.total_services}</TableCell>
                        <TableCell className="text-center text-green-600 font-medium">{eng.completed}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline">{eng.completion_rate}%</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!topEngineers?.engineers || topEngineers.engineers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-4">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Top Attendance Performers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-green-500" /> Top Attendance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-center">Present</TableHead>
                      <TableHead className="text-center">Hours</TableHead>
                      <TableHead className="text-right">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(topAttendance?.employees || []).map((emp, idx) => (
                      <TableRow key={emp.id}>
                        <TableCell>
                          {idx < 3 ? (
                            <Badge className={idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'}>
                              {idx + 1}
                            </Badge>
                          ) : idx + 1}
                        </TableCell>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell className="text-center">{emp.present_days}/{emp.working_days}</TableCell>
                        <TableCell className="text-center">{emp.total_hours}h</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={emp.attendance_percentage >= 90 ? 'default' : 'outline'} 
                                 className={emp.attendance_percentage >= 90 ? 'bg-green-500' : ''}>
                            {emp.attendance_percentage}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!topAttendance?.employees || topAttendance.employees.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-4">No data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Performance Charts */}
          <div className="grid grid-cols-2 gap-6">
            {/* Sales Executives Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sales Executive Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topSales?.executives?.slice(0, 5) || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="converted" fill="#10b981" name="Converted" />
                    <Bar dataKey="in_progress" fill="#f59e0b" name="In Progress" />
                    <Bar dataKey="lost" fill="#ef4444" name="Lost" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Service Engineers Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Service Engineer Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topEngineers?.engineers?.slice(0, 5) || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" fill="#10b981" name="Completed" />
                    <Bar dataKey="in_progress" fill="#f59e0b" name="In Progress" />
                    <Bar dataKey="pending" fill="#ef4444" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OwnerDashboard;
